<template>
  <div class="summary-page">
    <!-- Loading State -->
    <div v-if="isLoading" class="loading-screen">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p>{{ $t("qbank.summary.loading") }}</p>
      </div>
    </div>

    <!-- Login Required -->
    <div v-else-if="!isLoggedIn" class="login-required">
      <div class="login-card">
        <div class="login-icon">
          <i class="bi bi-person-badge"></i>
        </div>
        <h3>{{ $t("qbank.summary.please_login") }}</h3>
        <a href="login" class="btn-login">
          {{ $t("qbank.summary.login") }}
          <i class="fa fa-sign-in"></i>
        </a>
      </div>
    </div>

    <!-- Main Content -->
    <div v-else class="summary-content">
      <!-- Header -->
      <div class="summary-header">
        <div class="header-background"></div>
        <div class="header-content">
          <button @click="goBack" class="btn-back">
            <i class="fas fa-arrow-left"></i>
            {{ $t("qbank.summary.back") }}
          </button>
          <div class="header-title">
            <div class="title-icon">
              <i class="fas fa-chart-line"></i>
            </div>
            <div>
              <h1>{{ $t("qbank.summary.test_summary") }}</h1>
              <p>{{ $t("qbank.summary.platform_name") }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Score Circle Section -->
      <div class="score-section">
        <div class="score-card main-score">
          <div
            class="score-circle"
            :class="getScoreClass(calculateCorrectPercentage())"
          >
            <svg viewBox="0 0 100 100">
              <circle class="score-bg" cx="50" cy="50" r="45" />
              <circle
                class="score-progress"
                cx="50"
                cy="50"
                r="45"
                :style="{
                  strokeDashoffset: getStrokeDashoffset(
                    calculateCorrectPercentage()
                  ),
                }"
              />
            </svg>
            <div class="score-value">
              <span class="percentage">{{ calculateCorrectPercentage() }}</span>
              <span class="percent-sign">%</span>
            </div>
          </div>
          <div class="score-label">
            {{ $t("qbank.summary.correct_percentage") }}
          </div>
        </div>
      </div>

      <!-- Stats Cards -->
      <div class="stats-grid">
        <div class="stat-card correct">
          <div class="stat-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ totals.correct_answers }}</span>
            <span class="stat-label">{{
              $t("qbank.summary.correct_answers")
            }}</span>
          </div>
        </div>

        <div class="stat-card incorrect">
          <div class="stat-icon">
            <i class="fas fa-times-circle"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ totals.incorrect_answers }}</span>
            <span class="stat-label">{{
              $t("qbank.summary.incorrect_answers")
            }}</span>
          </div>
        </div>

        <div class="stat-card total">
          <div class="stat-icon">
            <i class="fas fa-list-check"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value"
              >{{ totals.answered_questions }}/{{
                totals.total_questions
              }}</span
            >
            <span class="stat-label">{{
              $t("qbank.summary.answered_questions")
            }}</span>
          </div>
        </div>

        <div class="stat-card time">
          <div class="stat-icon">
            <i class="fas fa-clock"></i>
          </div>
          <div class="stat-info">
            <span class="stat-value">{{ formatTime(totals.actual_time) }}</span>
            <span class="stat-label">{{
              $t("qbank.summary.actual_time")
            }}</span>
          </div>
        </div>
      </div>

      <!-- Details Section -->
      <div class="details-section">
        <!-- Totals Card -->
        <div class="detail-card totals-card">
          <div class="card-header">
            <i class="fas fa-chart-pie"></i>
            <h3>{{ $t("qbank.summary.totals") }}</h3>
          </div>
          <div class="card-body">
            <div class="detail-row">
              <div class="detail-icon total-q">
                <i class="fas fa-list-ol"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.total_questions")
              }}</span>
              <span class="detail-value">{{ totals.total_questions }}</span>
            </div>
            <div class="detail-row">
              <div class="detail-icon answered">
                <i class="fas fa-check-double"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.answered_questions")
              }}</span>
              <span class="detail-value">{{ totals.answered_questions }}</span>
            </div>
            <div class="detail-row">
              <div class="detail-icon unanswered">
                <i class="fas fa-hourglass-half"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.unanswered_questions")
              }}</span>
              <span class="detail-value">{{
                totals.unanswered_questions
              }}</span>
            </div>
            <div class="detail-row">
              <div class="detail-icon correct">
                <i class="fas fa-thumbs-up"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.correct_answers")
              }}</span>
              <span class="detail-value success">{{
                totals.correct_answers
              }}</span>
            </div>
            <div class="detail-row">
              <div class="detail-icon incorrect">
                <i class="fas fa-thumbs-down"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.incorrect_answers")
              }}</span>
              <span class="detail-value danger">{{
                totals.incorrect_answers
              }}</span>
            </div>
            <div class="detail-row" v-if="isTimedMode">
              <div class="detail-icon correct-update">
                <i class="fas fa-edit"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.correct_after_update")
              }}</span>
              <span class="detail-value success">{{
                totals.correct_after_update
              }}</span>
            </div>
            <div class="detail-row">
              <div class="detail-icon time-est">
                <i class="fas fa-hourglass-start"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.estimated_time")
              }}</span>
              <span class="detail-value"
                >{{ formatTime(totals.estimated_time) }}
                {{ $t("qbank.summary.minutes") }}</span
              >
            </div>
            <div class="detail-row">
              <div class="detail-icon time-act">
                <i class="fas fa-stopwatch"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.actual_time")
              }}</span>
              <span class="detail-value"
                >{{ formatTime(totals.actual_time) }}
                {{ $t("qbank.summary.minutes") }}</span
              >
            </div>
            <div class="detail-row">
              <div
                class="detail-icon time-diff"
                :class="timeDifference >= 0 ? 'time-over' : 'time-under'"
              >
                <i class="fas fa-clock"></i>
              </div>
              <span class="detail-label">{{
                $t("qbank.summary.time_difference")
              }}</span>
              <span
                class="detail-value"
                :class="timeDifference >= 0 ? 'danger' : 'success'"
              >
                {{ timeDifference >= 0 ? "+" : ""
                }}{{ formatTime(Math.abs(timeDifference)) }}
                {{ $t("qbank.summary.minutes") }}
              </span>
            </div>
          </div>
        </div>

        <!-- Lessons Card -->
        <div
          class="detail-card lessons-card"
          v-if="lessons && lessons.length > 0"
        >
          <div class="card-header">
            <i class="fas fa-book-open"></i>
            <h3>{{ $t("qbank.summary.lessons") }}</h3>
            <span class="lessons-count">{{ lessons.length }}</span>
          </div>
          <div class="card-body lessons-scroll">
            <div
              v-for="(lesson, index) in lessons"
              :key="index"
              class="lesson-item"
            >
              <div class="lesson-header">
                <span class="lesson-number">{{ index + 1 }}</span>
                <span class="lesson-title">{{ lesson.title }}</span>
                <span class="lesson-questions-count"
                  >{{ lesson.questions_count }}
                  {{ $t("qbank.summary.questions") || "سؤال" }}</span
                >
              </div>
              <div class="lesson-stats">
                <span class="lesson-stat correct">
                  <i class="fas fa-check"></i>
                  <span class="stat-count">{{ lesson.correct_answers }}</span>
                  <span class="stat-text">{{
                    $t("qbank.summary.correct")
                  }}</span>
                </span>
                <span class="lesson-stat incorrect">
                  <i class="fas fa-times"></i>
                  <span class="stat-count">{{ lesson.incorrect_answers }}</span>
                  <span class="stat-text">{{
                    $t("qbank.summary.incorrect")
                  }}</span>
                </span>
                <span class="lesson-stat unanswered">
                  <i class="fas fa-hourglass-half"></i>
                  <span class="stat-count">{{
                    lesson.unanswered_questions
                  }}</span>
                  <span class="stat-text">{{
                    $t("qbank.summary.unanswered")
                  }}</span>
                </span>
              </div>
              <div class="lesson-progress">
                <div class="progress-track-multi">
                  <div
                    class="progress-segment correct"
                    :style="{ width: getLessonCorrectWidth(lesson) + '%' }"
                  ></div>
                  <div
                    class="progress-segment incorrect"
                    :style="{ width: getLessonIncorrectWidth(lesson) + '%' }"
                  ></div>
                  <div
                    class="progress-segment unanswered"
                    :style="{ width: getLessonUnansweredWidth(lesson) + '%' }"
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- AI Analysis Section -->
      <div class="ai-section">
        <div class="ai-card">
          <div class="ai-header">
            <div class="ai-icon">
              <i class="fas fa-robot"></i>
            </div>
            <div class="ai-title">
              <h3>{{ $t("qbank.summary.performance_analysis") }}</h3>
              <p>تحليل ذكي لأدائك</p>
            </div>
          </div>
          <div class="ai-body">
            <button
              v-if="!performance_analysis && !loadingPerformance"
              class="btn-analyze"
              @click="performanceAnlysis"
            >
              <i class="fas fa-magic"></i>
              {{ $t("qbank.summary.click_for_analysis") }}
            </button>

            <div v-if="loadingPerformance" class="ai-loading">
              <div class="ai-loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
              <p>{{ $t("qbank.summary.loading_analysis") }}</p>
            </div>

            <div v-if="displayedText && !loadingPerformance" class="ai-result">
              <p>{{ displayedText }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiUrl, baseUrl } from "@/utils/api";
export default {
  name: "QbankSummary",

  data() {
    return {
      categories: [],
      subCategories: [],
      isLoading: true,
      loadingBalance: false,
      token: localStorage.getItem("access_token"),
      courseCount: 0,
      isLoggedIn: false,
      performance_analysis: null,
      displayedText: "",
      loadingPerformance: false,
      walletBalance: 0,
      catImagePath: baseUrl + "/images/category/",
      defaultImage: "/assets/img/acad-icon.png",
      qbankId: this.$route.query.qbank,
      subscriptionId: this.$route.query.subscriptionId || null,
      quizMode: this.$route.query.mode || null,
      fromMia: String(this.$route.query.fromMia) === "1",
      totals: {
        total_questions: 0,
        answered_questions: 0,
        unanswered_questions: 0,
        correct_answers: 0,
        incorrect_answers: 0,
        correct_after_update: 0,
        estimated_time: 0,
        actual_time: 0,
      },
      lessons: [],
    };
  },
  computed: {
    timeDifference() {
      const diff = this.totals.actual_time - this.totals.estimated_time;
      return diff;
    },
    isTimedMode() {
      return this.quizMode === "timed";
    },
  },
  mounted() {
    this.getSummary();
  },
  methods: {
    calculateCorrectPercentage() {
      return this.totals.total_questions
        ? (
            (this.totals.correct_answers / this.totals.total_questions) *
            100
          ).toFixed(2)
        : 0;
    },
    calculateIncorrectPercentage() {
      return this.totals.total_questions
        ? (
            (this.totals.incorrect_answers / this.totals.total_questions) *
            100
          ).toFixed(2)
        : 0;
    },
    calculateAnsweredPercentage() {
      return this.totals.total_questions
        ? (
            (this.totals.answered_questions / this.totals.total_questions) *
            100
          ).toFixed(2)
        : 0;
    },
    calculateLessonPercentage(lesson) {
      const total =
        lesson.questions_count ||
        lesson.total_questions ||
        lesson.correct_answers +
          lesson.incorrect_answers +
          lesson.unanswered_questions;
      if (!total || total === 0) return 0;
      return ((lesson.correct_answers / total) * 100).toFixed(0);
    },
    getLessonCorrectWidth(lesson) {
      const total =
        lesson.questions_count ||
        lesson.total_questions ||
        lesson.correct_answers +
          lesson.incorrect_answers +
          lesson.unanswered_questions;
      if (!total || total === 0) return 0;
      return ((lesson.correct_answers / total) * 100).toFixed(1);
    },
    getLessonIncorrectWidth(lesson) {
      const total =
        lesson.questions_count ||
        lesson.total_questions ||
        lesson.correct_answers +
          lesson.incorrect_answers +
          lesson.unanswered_questions;
      if (!total || total === 0) return 0;
      return ((lesson.incorrect_answers / total) * 100).toFixed(1);
    },
    getLessonUnansweredWidth(lesson) {
      const total =
        lesson.questions_count ||
        lesson.total_questions ||
        lesson.correct_answers +
          lesson.incorrect_answers +
          lesson.unanswered_questions;
      if (!total || total === 0) return 0;
      return ((lesson.unanswered_questions / total) * 100).toFixed(1);
    },
    formatTime(seconds) {
      if (!seconds || seconds === 0) return "0:00";
      const absSeconds = Math.abs(seconds);
      const mins = Math.floor(absSeconds / 60);
      const secs = absSeconds % 60;
      return `${mins}:${secs < 10 ? "0" + secs : secs}`;
    },
    getScoreClass(percentage) {
      const score = parseFloat(percentage);
      if (score >= 80) return "excellent";
      if (score >= 60) return "good";
      if (score >= 40) return "average";
      return "needs-improvement";
    },
    getStrokeDashoffset(percentage) {
      const circumference = 2 * Math.PI * 45;
      const score = parseFloat(percentage) || 0;
      return circumference - (score / 100) * circumference;
    },

    async getSummary() {
      this.loadingBalance = true;
      try {
        const response = await fetch(
          `${apiUrl}/qbanks/${this.qbankId}/summary`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch summary");
        }

        // إذا كانت الاستجابة ناجحة
        this.isLoggedIn = true;
        const data = await response.json();

        // تخزين البيانات المستخلصة في متغير totals
        this.totals = {
          ...data.totals,
          correct_after_update:
            data.totals.correct_answers_after_update ?? null,
        };
        // Set timed mode if correct_answers_after_update exists in API response
        if (data.totals.correct_answers_after_update !== undefined) {
          this.quizMode = "timed";
        }
        this.performance_analysis = data.performance_analysis;
        if (this.performance_analysis) {
          this.typeText(this.performance_analysis);
        }
        // تخزين الدروس
        this.lessons = data.lessons;
        // تخزين subscription_plan_id
        if (data.subscription_plan_id) {
          this.subscriptionId = data.subscription_plan_id;
        }
      } catch (error) {
        console.error("Error fetching summary:", error);
      } finally {
        this.loadingBalance = false;
        this.isLoading = false;
      }
    },
    async performanceAnlysis() {
      this.loadingPerformance = true;

      try {
        const response = await fetch(
          `${apiUrl}/qbanks/${this.qbankId}/performance-analysis`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${this.token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error("فشل في جلب تحليل الأداء");
        }

        const data = await response.json();
        this.performance_analysis = data.report;

        // بعد الحصول على البيانات، قم بعرض النص تدريجياً
        this.typeText(this.performance_analysis);
      } catch (error) {
        console.error("Error fetching summary:", error);
      } finally {
        this.loadingPerformance = false;
      }
    },
    typeText(text) {
      this.displayedText = "";
      let index = 0;

      const typingEffect = () => {
        if (index < text.length) {
          this.displayedText += text.charAt(index);
          index++;
          setTimeout(typingEffect, 25); // سرعة الكتابة
        }
      };
      typingEffect();
    },
    goBack() {
      // For Mia users, go back to Mia requests list
      if (this.fromMia) {
        this.$router.push({ name: "MiaV2RequestsList", query: { filter: "question_bank" } });
        return;
      }

      if (this.subscriptionId) {
        this.$router.push({
          path: "/qbank-list-quiz",
          query: { subscriptionId: this.subscriptionId },
        });
      } else {
        // إذا لم يكن هناك subscriptionId، نرجع للصفحة الرئيسية
        this.$router.push("/qbank");
      }
    },
  },
};
</script>

<style scoped>
/* Brand Colors */
:root {
  --brand-primary: #07c232;
  --brand-primary-dark: #05a627;
  --brand-primary-darker: #048a20;
  --brand-primary-light: #e8fbed;
}

/* Base Styles */
.summary-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #e8fbed 0%, #f0fff4 50%, #f8fafc 100%);
}

/* Loading Screen */
.loading-screen {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #e8fbed 0%, #f0fff4 100%);
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid #e5e7eb;
  border-top-color: #07c232;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-content p {
  color: #6b7280;
  font-size: 1.1rem;
}

/* Login Required */
.login-required {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.login-card {
  background: white;
  border-radius: 24px;
  padding: 50px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(7, 194, 50, 0.15);
  max-width: 400px;
  border: 1px solid rgba(7, 194, 50, 0.1);
}

.login-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #07c232, #05a627);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
}

.login-icon i {
  font-size: 36px;
  color: white !important;
}

.login-card h3 {
  color: #1f2937;
  margin-bottom: 24px;
  font-weight: 600;
}

.btn-login {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #07c232, #05a627);
  color: white;
  padding: 14px 32px;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
}

.btn-login:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(7, 194, 50, 0.3);
  color: white;
}

/* Summary Content */
.summary-content {
  padding-bottom: 40px;
}

/* Header */
.summary-header {
  position: relative;
  padding: 30px 20px 80px;
  overflow: hidden;
}

.header-background {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #07c232 0%, #05a627 50%, #048a20 100%);
  border-radius: 0 0 40px 40px;
}

.header-content {
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
}

.btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 24px;
}

.btn-back:hover {
  background: rgba(255, 255, 255, 0.3);
}

.header-title {
  display: flex;
  align-items: center;
  gap: 20px;
  color: white;
}

.title-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.title-icon i {
  font-size: 28px;
  color: white !important;
}

.header-title h1 {
  font-size: 1.8rem;
  font-weight: 700;
  margin: 0;
}

.header-title p {
  margin: 4px 0 0;
  opacity: 0.9;
  font-size: 0.95rem;
}

/* Score Section */
.score-section {
  max-width: 1200px;
  margin: -30px auto 30px;
  padding: 0 20px;
}

.score-card {
  background: white;
  border-radius: 24px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
}

.score-circle {
  width: 180px;
  height: 180px;
  margin: 0 auto 20px;
  position: relative;
}

.score-circle svg {
  transform: rotate(-90deg);
  width: 100%;
  height: 100%;
}

.score-circle .score-bg {
  fill: none;
  stroke: #e5e7eb;
  stroke-width: 8;
}

.score-circle .score-progress {
  fill: none;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 283;
  transition: stroke-dashoffset 1s ease-out;
}

.score-circle.excellent .score-progress {
  stroke: #07c232;
}
.score-circle.good .score-progress {
  stroke: #05a627;
}
.score-circle.average .score-progress {
  stroke: #f59e0b;
}
.score-circle.needs-improvement .score-progress {
  stroke: #ef4444;
}

.score-value {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: baseline;
}

.score-value .percentage {
  font-size: 3rem;
  font-weight: 700;
  color: #1f2937;
}

.score-value .percent-sign {
  font-size: 1.5rem;
  font-weight: 600;
  color: #6b7280;
}

.score-label {
  font-size: 1.1rem;
  color: #6b7280;
  font-weight: 500;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto 30px;
  padding: 0 20px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
}

.stat-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.1);
}

.stat-card .stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card .stat-icon i {
  font-size: 24px;
  color: white !important;
}

.stat-card.correct .stat-icon {
  background: linear-gradient(135deg, #07c232, #05a627);
}
.stat-card.incorrect .stat-icon {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}
.stat-card.total .stat-icon {
  background: linear-gradient(135deg, #07c232, #048a20);
}
.stat-card.time .stat-icon {
  background: linear-gradient(135deg, #05a627, #048a20);
}

.stat-card .stat-info {
  display: flex;
  flex-direction: column;
}

.stat-card .stat-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
}

.stat-card .stat-label {
  font-size: 0.85rem;
  color: #6b7280;
}

/* Details Section */
.details-section {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto 30px;
  padding: 0 20px;
}

.detail-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.detail-card .card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #07c232, #05a627);
  color: white;
}

.detail-card .card-header i {
  font-size: 20px;
  color: white !important;
}

.detail-card .card-header h3 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  flex: 1;
  color: white !important;
}

.lessons-count {
  background: rgba(255, 255, 255, 0.2);
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
}

.detail-card .card-body {
  padding: 8px 0;
}

/* Detail Rows */
.detail-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 14px 24px;
  transition: background-color 0.2s ease;
}

.detail-row:hover {
  background: #f9fafb;
}

.detail-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.detail-icon i {
  font-size: 16px;
  color: white !important;
}

.detail-icon.total-q {
  background: linear-gradient(135deg, #07c232, #05a627);
}
.detail-icon.answered {
  background: linear-gradient(135deg, #07c232, #05a627);
}
.detail-icon.unanswered {
  background: linear-gradient(135deg, #f59e0b, #d97706);
}
.detail-icon.correct {
  background: linear-gradient(135deg, #07c232, #05a627);
}
.detail-icon.incorrect {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}
.detail-icon.correct-update {
  background: linear-gradient(135deg, #3b82f6, #2563eb);
}
.detail-icon.time-est {
  background: linear-gradient(135deg, #05a627, #048a20);
}
.detail-icon.time-act {
  background: linear-gradient(135deg, #07c232, #048a20);
}
.detail-icon.time-diff.time-over {
  background: linear-gradient(135deg, #ef4444, #dc2626);
}
.detail-icon.time-diff.time-under {
  background: linear-gradient(135deg, #07c232, #05a627);
}

.detail-label {
  flex: 1;
  color: #4b5563;
  font-size: 0.95rem;
}

.detail-value {
  font-weight: 700;
  font-size: 1.1rem;
  color: #1f2937;
}

.detail-value.success {
  color: #07c232;
}
.detail-value.danger {
  color: #ef4444;
}

/* Lessons Scroll */
.lessons-scroll {
  max-height: 380px;
  overflow-y: auto;
  padding: 0 !important;
}

.lessons-scroll::-webkit-scrollbar {
  width: 6px;
}

.lessons-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
}

.lessons-scroll::-webkit-scrollbar-thumb {
  background: #07c232;
  border-radius: 3px;
}

.lesson-item {
  padding: 16px 24px;
  border-bottom: 1px solid #f1f5f9;
  transition: background-color 0.2s ease;
}

.lesson-item:last-child {
  border-bottom: none;
}

.lesson-item:hover {
  background: #e8fbed;
}

.lesson-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.lesson-number {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #07c232, #05a627);
  color: white;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 600;
  flex-shrink: 0;
}

.lesson-title {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1f2937;
  flex: 1;
}

.lesson-questions-count {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6b7280;
  background: #f3f4f6;
  padding: 4px 10px;
  border-radius: 12px;
}

.lesson-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 10px;
}

.lesson-stat {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 8px;
  background: #f8fafc;
}

.lesson-stat i {
  font-size: 0.7rem;
}

.lesson-stat .stat-count {
  font-weight: 700;
}

.lesson-stat .stat-text {
  font-weight: 500;
  font-size: 0.75rem;
}

.lesson-stat.correct {
  color: #07c232;
  background: #e8fbed;
}
.lesson-stat.incorrect {
  color: #ef4444;
  background: #fef2f2;
}
.lesson-stat.unanswered {
  color: #6b7280;
  background: #f3f4f6;
}

.lesson-progress {
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-track {
  flex: 1;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.progress-track-multi {
  flex: 1;
  height: 10px;
  background: #e5e7eb;
  border-radius: 5px;
  overflow: hidden;
  display: flex;
}

.progress-segment {
  height: 100%;
  transition: width 0.5s ease;
}

.progress-segment.correct {
  background: linear-gradient(90deg, #07c232, #05a627);
}

.progress-segment.incorrect {
  background: linear-gradient(90deg, #ef4444, #dc2626);
}

.progress-segment.unanswered {
  background: linear-gradient(90deg, #9ca3af, #6b7280);
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #07c232, #05a627);
  border-radius: 4px;
  transition: width 0.5s ease;
}

.progress-value {
  font-size: 0.85rem;
  font-weight: 700;
  color: #07c232;
  min-width: 45px;
  text-align: right;
}

/* AI Section */
.ai-section {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.ai-card {
  background: white;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
}

.ai-header {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 24px;
  background: linear-gradient(135deg, #07c232, #05a627);
  color: white;
}

.ai-icon {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-icon i {
  font-size: 28px;
  color: white !important;
}

.ai-title h3 {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
}

.ai-title p {
  margin: 4px 0 0;
  opacity: 0.9;
  font-size: 0.9rem;
}

.ai-body {
  padding: 30px;
  text-align: center;
}

.btn-analyze {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #07c232, #05a627);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-analyze:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(7, 194, 50, 0.3);
}

.ai-loading {
  padding: 20px;
}

.ai-loading-dots {
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
}

.ai-loading-dots span {
  width: 12px;
  height: 12px;
  background: #07c232;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.ai-loading-dots span:nth-child(1) {
  animation-delay: -0.32s;
}
.ai-loading-dots span:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes bounce {
  0%,
  80%,
  100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}

.ai-loading p {
  color: #6b7280;
}

.ai-result {
  text-align: right;
  padding: 20px;
  background: #f9fafb;
  border-radius: 16px;
}

.ai-result p {
  color: #4b5563;
  line-height: 1.8;
  margin: 0;
}

/* Dark Mode */
[data-bs-theme="dark"] .summary-page,
.dark .summary-page {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
}

[data-bs-theme="dark"] .score-card,
[data-bs-theme="dark"] .stat-card,
[data-bs-theme="dark"] .detail-card,
[data-bs-theme="dark"] .ai-card,
.dark .score-card,
.dark .stat-card,
.dark .detail-card,
.dark .ai-card {
  background: #1e293b;
}

[data-bs-theme="dark"] .score-value .percentage,
[data-bs-theme="dark"] .stat-card .stat-value,
[data-bs-theme="dark"] .detail-value,
[data-bs-theme="dark"] .lesson-title,
.dark .score-value .percentage,
.dark .stat-card .stat-value,
.dark .detail-value,
.dark .lesson-title {
  color: #f1f5f9;
}

[data-bs-theme="dark"] .score-label,
[data-bs-theme="dark"] .stat-card .stat-label,
[data-bs-theme="dark"] .detail-label,
.dark .score-label,
.dark .stat-card .stat-label,
.dark .detail-label {
  color: #94a3b8;
}

[data-bs-theme="dark"] .detail-row:hover,
[data-bs-theme="dark"] .lesson-item:hover,
.dark .detail-row:hover,
.dark .lesson-item:hover {
  background: #334155;
}

[data-bs-theme="dark"] .lesson-item,
.dark .lesson-item {
  border-bottom-color: #334155;
}

[data-bs-theme="dark"] .progress-track,
[data-bs-theme="dark"] .progress-track-multi,
[data-bs-theme="dark"] .score-circle .score-bg,
.dark .progress-track,
.dark .progress-track-multi,
.dark .score-circle .score-bg {
  background: #334155;
  stroke: #334155;
}

[data-bs-theme="dark"] .lesson-questions-count,
.dark .lesson-questions-count {
  background: #475569;
  color: #94a3b8;
}

[data-bs-theme="dark"] .lesson-stat.correct,
.dark .lesson-stat.correct {
  background: rgba(7, 194, 50, 0.15);
}

[data-bs-theme="dark"] .lesson-stat.incorrect,
.dark .lesson-stat.incorrect {
  background: rgba(239, 68, 68, 0.15);
}

[data-bs-theme="dark"] .lesson-stat.unanswered,
.dark .lesson-stat.unanswered {
  background: #475569;
}

[data-bs-theme="dark"] .ai-result,
.dark .ai-result {
  background: #334155;
}

[data-bs-theme="dark"] .ai-result p,
.dark .ai-result p {
  color: #cbd5e1;
}

/* Responsive */
@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .details-section {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .summary-header {
    padding: 20px 16px 70px;
  }

  .header-title h1 {
    font-size: 1.4rem;
  }

  .score-card {
    padding: 30px 20px;
  }

  .score-circle {
    width: 150px;
    height: 150px;
  }

  .score-value .percentage {
    font-size: 2.5rem;
  }

  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }

  .stat-card {
    padding: 16px;
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .stat-card .stat-value {
    font-size: 1.3rem;
  }

  .detail-row {
    padding: 12px 16px;
  }

  .lesson-item {
    padding: 14px 16px;
  }

  .ai-body {
    padding: 20px;
  }
}

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr 1fr;
  }

  .stat-card .stat-icon {
    width: 44px;
    height: 44px;
  }

  .stat-card .stat-icon i {
    font-size: 20px;
  }
}
</style>
